import { createReducer, on } from '@ngrx/store';

import { klona } from 'klona/json';

import { DataList } from '@core/interfaces';
import { setColumnList, setColumnSize } from '@util/grid';
import { currentTimeInSeconds } from '@util/list';

import { Grids } from '#types/grid';

import * as GridActions from './grid.actions';

export interface GridState {
  sidebar: 'expanded' | 'collapsed';
  panels: {
    filters: 'open' | 'closed';
    columnSettings: 'open' | 'closed';
    exportData: 'open' | 'closed';
    saveGrid: 'open' | 'closed';
    loadGrid: 'open' | 'closed';
  };
  saved: Record<string, DataList>;
  grids: Grids;
}

export const initialState: GridState = {
  sidebar: 'expanded',
  panels: {
    filters: 'closed',
    columnSettings: 'closed',
    exportData: 'closed',
    saveGrid: 'closed',
    loadGrid: 'closed',
  },
  saved: {},
  grids: {},
};

// default reducers for common actions
const defaultReducer = createReducer(
  initialState,

  on(
    GridActions.openSidebar,
    (state): GridState => ({
      ...state,
      sidebar: 'expanded',
    })
  ),

  on(
    GridActions.closeSidebar,
    (state): GridState => ({
      ...state,
      sidebar: 'collapsed',
    })
  ),

  on(
    GridActions.initPanel,
    (state): GridState => ({
      ...state,
      panels: initialState.panels,
    })
  ),

  on(
    GridActions.resetPanel,
    (state): GridState => ({
      ...state,
      panels: initialState.panels,
    })
  ),

  on(
    GridActions.openFilterPanel,
    (state): GridState => ({
      ...state,
      panels: {
        ...state.panels,
        filters: 'open',
      },
    })
  ),

  on(
    GridActions.closeFilterPanel,
    (state): GridState => ({
      ...state,
      panels: {
        ...state.panels,
        filters: 'closed',
      },
    })
  ),

  on(
    GridActions.openColumnPanel,
    (state): GridState => ({
      ...state,
      panels: {
        ...state.panels,
        columnSettings: 'open',
      },
    })
  ),

  on(
    GridActions.closeColumnPanel,
    (state): GridState => ({
      ...state,
      panels: {
        ...state.panels,
        columnSettings: 'closed',
      },
    })
  ),

  on(
    GridActions.openExportPanel,
    (state): GridState => ({
      ...state,
      panels: {
        ...state.panels,
        exportData: 'open',
      },
    })
  ),

  on(
    GridActions.closeExportPanel,
    (state): GridState => ({
      ...state,
      panels: {
        ...state.panels,
        exportData: 'closed',
      },
    })
  ),

  on(
    GridActions.openSaveGridPanel,
    (state): GridState => ({
      ...state,
      panels: {
        ...state.panels,
        saveGrid: 'open',
      },
    })
  ),

  on(
    GridActions.closeSaveGridPanel,
    (state): GridState => ({
      ...state,
      panels: {
        ...state.panels,
        saveGrid: 'closed',
      },
    })
  ),

  on(
    GridActions.openLoadGridPanel,
    (state): GridState => ({
      ...state,
      panels: {
        ...state.panels,
        loadGrid: 'open',
      },
    })
  ),

  on(
    GridActions.closeLoadGridPanel,
    (state): GridState => ({
      ...state,
      panels: {
        ...state.panels,
        loadGrid: 'closed',
      },
    })
  ),

  //
  // grid state save / load / remove
  //

  on(
    GridActions.ListRequest,
    (state, action): GridState => ({
      ...state,
      saved: {
        ...state.saved,
        [action.id]: null,
      },
    })
  ),

  on(
    GridActions.ListRequestSuccess,
    (state, action): GridState => ({
      ...state,
      saved: {
        ...state.saved,
        [action.id]: {
          data: [...action.list],
          updated: currentTimeInSeconds(),
        },
      },
    })
  ),

  //
  // grid instances
  //

  on(
    GridActions.InitializeGridComplete,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          availableColumns: [...action.grid.availableColumns],
          selectedColumns: [...action.grid.selectedColumns],
          filters: {
            ...action.grid.filters,
          },
          first: !action.grid.first || action.grid.first === 0 ? action.defaults.first : action.grid.first,
          initialized: true,
          page: !action.grid.page || action.grid.page === 0 ? action.defaults.page : action.grid.page,
          rows: !action.grid.rows || action.grid.rows === 0 ? action.defaults.rows : action.grid.rows,
          selectedRow: null,
          selectedRows: null,
          summaryRow: null,
          summaryRowText: null,
          sorts: {
            ...action.grid.sorts,
          },
          defaultFilters: {
            ...action.defaults.defaultFilters,
          },
          defaultSorts: {
            ...action.defaults.defaultSorts,
          },
          defaultAvailableColumns: [...action.defaults.defaultAvailableColumns],
          defaultSelectedColumns: [...action.defaults.defaultSelectedColumns],
          saveId: 'default',
          saveName: null,
        },
      },
    })
  ),

  on(
    GridActions.Reset,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          availableColumns: klona(state.grids[action.id].defaultAvailableColumns),
          selectedColumns: klona(state.grids[action.id].defaultSelectedColumns),
          filters: klona(state.grids[action.id].defaultFilters),
          first: 0,
          page: 1,
          selectedRow: null,
          selectedRows: null,
          summaryRow: null,
          summaryRowText: null,
          sorts: klona(state.grids[action.id].defaultSorts),
          saveId: 'default',
          saveName: null,
        },
      },
    })
  ),

  on(
    GridActions.LoadSettings,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          availableColumns: [...action.grid.availableColumns],
          selectedColumns: [...action.grid.selectedColumns],
          filters: {
            ...action.grid.filters,
          },
          first: 0,
          page: 1,
          rows: action.grid.rows,
          selectedRow: null,
          selectedRows: null,
          summaryRow: null,
          summaryRowText: null,
          sorts: {
            ...action.grid.sorts,
          },
          saveId: action.saveId,
          saveName: action.saveName,
        },
      },
    })
  ),

  on(
    GridActions.SaveRequestSuccess,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          saveId: action.saveId,
          saveName: action.saveName,
        },
      },
    })
  ),

  on(
    GridActions.Destroy,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: null,
      },
    })
  ),

  on(GridActions.DestroyMultiple, (state, action): GridState => {
    if (!action.list || action.list.length === 0) {
      return {
        ...state,
        grids: {
          ...state.grids,
        },
      };
    }

    const grids = { ...state.grids };

    for (const id of action.list) {
      if (grids[id]) {
        grids[id] = null;
      }
    }

    return {
      ...state,
      grids: {
        ...grids,
      },
    };
  }),

  on(
    GridActions.SetData,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          data: action.data ? [...action.data] : [],
          totalRows: action.data ? action.data[0]?.meta?.total_rows ?? 0 : 0,
          selectedRow: null,
          selectedRows: null,
          summaryRow: null,
          summaryRowText: null,
        },
      },
    })
  ),

  on(
    GridActions.SetSummaryRow,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          summaryRow: action.summaryRow,
        },
      },
    })
  ),

  on(
    GridActions.SetSummaryRowText,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          summaryRowText: action.summaryRowText,
        },
      },
    })
  ),

  on(
    GridActions.SetFilters,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          filters: {
            ...action.filters,
          },
          page: 1,
          first: 0,
          selectedRow: null,
          selectedRows: null,
        },
      },
    })
  ),

  on(
    GridActions.SetDefaultFilters,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          defaultFilters: {
            ...action.filters,
          },
        },
      },
    })
  ),

  on(
    GridActions.OverrideFilters,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          defaultFilters: {
            ...action.filters,
          },
          filters: {
            ...action.filters,
          },
          page: 1,
          first: 0,
          selectedRow: null,
          selectedRows: null,
        },
      },
    })
  ),

  on(
    GridActions.SetPage,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          page: action.page,
          first: action.first,
          rows: action.rows,
          selectedRow: null,
          selectedRows: null,
          summaryRow: null,
          summaryRowText: null,
        },
      },
    })
  ),

  on(
    GridActions.SetColumns,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          availableColumns: [...action.availableColumns],
          selectedColumns: [...action.selectedColumns],
        },
      },
    })
  ),

  on(
    GridActions.SetColumnSize,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          selectedColumns: [...setColumnSize([...state.grids[action.id].selectedColumns], action.column, action.size)],
        },
      },
    })
  ),

  on(
    GridActions.SelectRow,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          selectedRow: {
            ...action.selectedRow,
          },
        },
      },
    })
  ),

  on(
    GridActions.ClearSelectedRow,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          selectedRow: null,
        },
      },
    })
  ),

  on(
    GridActions.SelectMultipleRows,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          selectedRows: {
            ...action.rows,
          },
        },
      },
    })
  ),

  on(
    GridActions.ClearMultipleRows,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          selectedRows: null,
        },
      },
    })
  ),

  on(
    GridActions.SetDropdownListValues,
    (state, action): GridState => ({
      ...state,
      grids: {
        ...state.grids,
        [action.id]: {
          ...state.grids[action.id],
          availableColumns: [...setColumnList([...state.grids[action.id].availableColumns], action.column, [...action.list])],
          selectedColumns: [...setColumnList([...state.grids[action.id].selectedColumns], action.column, [...action.list])],
        },
      },
    })
  )
);

//
// reducers that depend on module specific actions for state updates
// matching on the action.type string instead of using action creators
// this allows for matching against actions from multiple modules without
// importing the action classes for each individual module and building
// a gigantic on(....) reducer
//

export function reducer(state: GridState, action: any) {
  const type: string = action.type;

  if (type.includes('Grid Request Data')) {
    if (type.includes('Grid Request Data With Parameters')) {
      return {
        ...state,
        grids: {
          ...state.grids,
          [action.id]: {
            ...state.grids[action.id],
            filters: {
              ...action.filters,
            },
            page: action.page,
            first: action.first,
            rows: action.rows,
            sorts: {
              ...action.sorts,
            },
            selectedRow: null,
            selectedRows: null,
            summaryRow: null,
            summaryRowText: null,
          },
        },
      };
    } else if (type.includes('Grid Request Data With Filters')) {
      return {
        ...state,
        grids: {
          ...state.grids,
          [action.id]: {
            ...state.grids[action.id],
            filters: {
              ...action.filters,
            },
            page: 1,
            first: 0,
            selectedRow: null,
            selectedRows: null,
            summaryRow: null,
            summaryRowText: null,
          },
        },
      };
    } else if (type.includes('Grid Request Data Success')) {
      return {
        ...state,
        grids: {
          ...state.grids,
          [action.id]: {
            ...state.grids[action.id],
            data: action.data ? [...action.data] : [],
            totalRows: action.data ? action.data[0]?.meta?.total_rows ?? 0 : 0,
          },
        },
      };
    } else if (type.includes('Grid Request Data Failure')) {
      return {
        ...state,
        grids: {
          ...state.grids,
          [action.id]: {
            ...state.grids[action.id],
            data: [],
            totalRows: 0,
            summaryRow: null,
            summaryRowText: null,
          },
        },
      };
    } else {
      return {
        ...state,
        grids: {
          ...state.grids,
          [action.id]: {
            ...state.grids[action.id],
            selectedRow: null,
            selectedRows: null,
            summaryRow: null,
            summaryRowText: null,
          },
        },
      };
    }
  }

  return defaultReducer(state, action);
}
